import { TRIP_TYPE_CONFIG, type TripType } from '@ixigo/cab-core/location';
import Calendar from '@ixigo/icons/Calendar';
import LocationOn from '@ixigo/icons/LocationOn';
import Button from '@ixigo/iui/Button';
import { toast } from '@ixigo/iui/Toast';
import { useMediaQuery } from '@ixigo/iui/utils';
import { DisplayField } from '@repo/shared/components';
import { startSmartViewTransition } from '@repo/shared/utils/viewTransition';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
	DateTimePickerSheet,
	formatDateTimeLabel,
} from '@/components/date-time-picker';
import { SegmentedControl } from '@/components/segmented-control';
import { LocationPickerSheet } from '@/lib/location';
import { useModalState } from '@/lib/navigation/useModalState';
import { isAfterMinimumDuration } from '@/routes/-home/-utils/date-validation';
import {
	formatDuration,
	minutesToDuration,
} from '@/routes/-home/-utils/format-duration';
import { fetchTripType, type TripTypeResponse } from './api';
import { useSearchFormActions, useSearchFormData } from './store/selectors';
import type { FormValidationResult, SearchFormData } from './store/types';
import { searchTracker } from './tracker';

// Segmented-control options derived from the trip-type config (label per type).
const tripTypeOptions = (Object.keys(TRIP_TYPE_CONFIG) as TripType[]).map(
	(value) => ({ value, label: TRIP_TYPE_CONFIG[value].label }),
);

// Scope for the swap view transition — only the two location rows (tagged
// `data-vt` within this scope) participate, so the browser FLIP-animates each
// from its old slot to its new one when the trip type toggles.
const SWAP_VT_SCOPE = 'cab-search-swap';

export interface SearchFormProps
	extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onSubmit'> {
	initialData?: Partial<SearchFormData>;
	onSubmit: (validationResult: FormValidationResult) => void;
}

export function SearchForm({
	initialData,
	onSubmit,
	...props
}: SearchFormProps) {
	const formData = useSearchFormData();
	const { tripType } = formData;
	const actions = useSearchFormActions();
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
		defaultMatches: false,
	});
	const {
		airportField,
		placeholders,
		// Airport field icon reflects direction: takeoff (To Airport) / landing (From Airport)
		flightIcon: AirportIcon,
	} = TRIP_TYPE_CONFIG[tripType];

	const [tripTypeInfo, setTripTypeInfo] = useState<
		TripTypeResponse['data'] | null
	>(null);
	// Guards against a second toggle landing while a swap transition is still
	// animating (which would interrupt it and strip the rows' transition names).
	const swapInFlight = useRef(false);

	// Location picker modal — the `loc` param carries which field is active,
	// so the picker can be opened on a specific field from outside the form too.
	const { value: pickerField, setOpen: setPickerOpen } = useModalState('loc', {
		parse: (v) => v,
	});
	const activeField =
		pickerField === 'pickup' || pickerField === 'drop'
			? pickerField
			: undefined;

	const [showDateTimePicker, setShowDateTimePicker] = useState(false);
	const autoOpenCallbackRef = useRef<() => void>(null);

	// Initialize form data if provided
	useEffect(() => {
		if (initialData) {
			actions.updateFormData(initialData);
		}
	}, [initialData, actions]);

	// Format the ISO date-time string into a readable label (e.g., "Mon, 23 Jul • 6:30 AM")
	const formattedDateTime = useMemo(
		() => formatDateTimeLabel(formData.dateTime),
		[formData.dateTime],
	);

	// Toggle trip type via the segmented control. `setTripType` swaps pickup↔drop
	// + tripType atomically; the airport and city rows then reorder. Running that
	// flip through startSmartViewTransition lets the browser FLIP-animate each row
	// (tagged `data-vt`) from its old slot to its new one — the airport row keeps
	// its content and slides, the city row's label cross-fades as it moves. The
	// util sets/clears the view-transition-names around the swap; reduced motion
	// skips the transition entirely.
	const handleTripTypeChange = (next: TripType) => {
		if (next === tripType) return;
		if (reducedMotion) {
			actions.setTripType(next);
			return;
		}
		// Ignore toggles while a swap is still animating so we don't interrupt the
		// in-flight transition. Released a touch after the ~300ms group animation.
		if (swapInFlight.current) {
			actions.setTripType(next);
			return;
		}
		swapInFlight.current = true;
		setTimeout(() => {
			swapInFlight.current = false;
		}, 350);
		startSmartViewTransition({
			trigger: () => {
				actions.setTripType(next);
			},
			context: { scope: SWAP_VT_SCOPE },
		});
	};

	const handleSubmit = (updatedFormData?: SearchFormData) => {
		const { pickup, drop, dateTime } = updatedFormData || formData;
		if (!pickup) {
			handlePickupClick();
			return;
		}
		if (!drop) {
			handleDestinationClick();
			return;
		}
		const pickupTime = dateTime;
		// if pickupTime is half hour past from now, show error
		const isAfterAllowableDuration = isAfterMinimumDuration(
			pickupTime,
			tripTypeInfo?.minDurationInMinutes || 30,
		);
		if (!isAfterAllowableDuration) {
			toast.error(
				`Please select a time after ${formatDuration(minutesToDuration(tripTypeInfo?.minDurationInMinutes || 30))}`,
				{
					position: 'bottom-center',
					duration: 2000,
					containerStyle: {
						bottom: 80,
					},
				},
			);
			openDateTimePicker();
			return;
		}
		const validationResult = actions.validateForm();
		if (validationResult.isValid) {
			searchTracker.tracker.search();
		}
		// The near-airport check now happens server-side in fareDetails; the
		// cab-selection sheet renders the confirmation if the backend returns one.
		onSubmit(validationResult);
	};

	const handlePickupClick = () => {
		setPickerOpen({ open: true, value: 'pickup' });
	};

	const handleDestinationClick = () => {
		setPickerOpen({ open: true, value: 'drop' });
	};

	const updateTripTypeInfo = () => {
		const { pickup, drop } = formData;
		if (!pickup || !drop) {
			return;
		}
		fetchTripType({ pickup, drop }).then((response) => {
			if (!('errors' in response)) {
				setTripTypeInfo(response.data);
			}
		});
	};

	const toggleDateTimePicker = () => {
		if (!showDateTimePicker) {
			updateTripTypeInfo();
		}
		setShowDateTimePicker((prev) => !prev);
	};

	const openDateTimePicker = () => {
		updateTripTypeInfo();
		setShowDateTimePicker(true);
	};

	// Two persistent rows keyed by logical role. Their render ORDER follows the
	// trip type (airport on top for "From Airport", city on top for "To Airport"),
	// so toggling reorders the DOM and the view transition slides each into place.
	// The `vt` name is the stable view-transition identity per role; the field IS
	// the picker value, so onClick opens the picker on the row's own field.
	const buildRow = (
		role: 'airport' | 'city',
		vt: string,
		icon: React.ReactNode,
		field: 'pickup' | 'drop',
	) => ({
		role,
		vt,
		icon,
		value: formData[field]?.mainText,
		placeholder: placeholders[field],
		onClick: () => setPickerOpen({ open: true, value: field }),
	});
	const cityField = airportField === 'pickup' ? 'drop' : 'pickup';
	const airportRow = buildRow(
		'airport',
		'cab-airport-row',
		<AirportIcon />,
		airportField,
	);
	const cityRow = buildRow('city', 'cab-city-row', <LocationOn />, cityField);
	const locationRows =
		airportField === 'pickup' ? [airportRow, cityRow] : [cityRow, airportRow];

	return (
		<div className="bg-primary mx-20 w-auto rounded-20 relative" {...props}>
			<div className="border border-tertiary rounded-t-20 overflow-hidden">
				<div className="px-20 py-15px bg-primary">
					<SegmentedControl
						aria-label="Cab type"
						options={tripTypeOptions}
						value={tripType}
						onChange={handleTripTypeChange}
						className="w-full"
					/>
				</div>
				<div className="h-0 border-b border-disabled" />
				{locationRows.map((row, index) => (
					<Fragment key={row.role}>
						{index > 0 && <div className="h-0 border-b border-disabled" />}
						<div data-vt={row.vt} data-vt-scope={SWAP_VT_SCOPE}>
							<DisplayField
								icon={row.icon}
								value={row.value}
								placeholder={row.placeholder}
								onClick={row.onClick}
								className="px-15px"
							/>
						</div>
					</Fragment>
				))}
				<div className="h-0 border-b border-disabled" />

				<DisplayField
					icon={<Calendar />}
					value={formattedDateTime}
					placeholder="Pickup Date & Time"
					onClick={toggleDateTimePicker}
					className="px-15px"
				/>
			</div>

			<Button
				variant="solid"
				color="brand"
				size="lg"
				className="w-full rounded-b-20 rounded-t-none"
				onClick={() => handleSubmit()}
			>
				Book a Cab
			</Button>

			<LocationPickerSheet
				open={Boolean(activeField)}
				onClose={() => {
					setPickerOpen({ open: false });
				}}
				activeField={activeField}
				defaultValue={{ pickup: formData.pickup, drop: formData.drop }}
				tripType={tripType}
				onComplete={({ pickup, drop, tripType: pickerTripType }) => {
					actions.updateFormData({
						pickup: pickup,
						drop: drop,
						tripType: pickerTripType,
					});
					if (!formData.dateTime) {
						autoOpenCallbackRef.current = openDateTimePicker;
					}
				}}
				TransitionProps={{
					onExited: () => {
						const callback = autoOpenCallbackRef.current;
						autoOpenCallbackRef.current = null;
						setTimeout(() => {
							callback?.();
						});
					},
				}}
				mapApiKey={import.meta.env.PUBLIC_MAP_KEY}
			/>
			<DateTimePickerSheet
				open={showDateTimePicker}
				onClose={toggleDateTimePicker}
				defaultValue={formData.dateTime}
				minDurationInMinutes={tripTypeInfo?.minDurationInMinutes}
				alertMsg={tripTypeInfo?.msg}
				onConfirm={(value) => {
					const updatedFormData = actions.updateFormData({
						dateTime: value.toISOString(),
					});
					setShowDateTimePicker(false);
					handleSubmit(updatedFormData);
				}}
			/>
		</div>
	);
}
